import { Factory, trait } from 'miragejs';
import { faker } from '@faker-js/faker';
import { format } from 'date-fns';
import { DATE_FORMAT } from 'garaje/models/abstract/abstract-document';

export default Factory.extend({
  createdAt: () => faker.date.recent(),
  updatedAt: () => faker.date.recent(),

  withAttachments: trait({
    afterCreate(userDocument, server) {
      const userDocumentTemplateAttachments =
        userDocument?.userDocumentTemplate?.userDocumentTemplateAttachments?.models || [];

      userDocumentTemplateAttachments.forEach((userDocumentTemplateAttachment) => {
        server.create('user-document-attachment', {
          userDocument,
          userDocumentTemplateAttachment,
        });
      });
    },
  }),

  asApproved: trait({
    afterCreate(userDocument, server) {
      server.create('user-document-link', 'asApproved', {
        userDocument,
        createdAt: userDocument.createdAt,
      });
    },
  }),

  asDenied: trait({
    afterCreate(userDocument, server) {
      server.create('user-document-link', 'asDenied', {
        userDocument,
        createdAt: userDocument.createdAt,
      });
    },
  }),

  asReview: trait({
    afterCreate(userDocument, server) {
      server.create('user-document-link', 'asReview', {
        userDocument,
        createdAt: userDocument.createdAt,
      });
    },
  }),

  withUser: trait({
    afterCreate(userDocument, server) {
      server.create('user', 'asEmployee', { userDocuments: [userDocument] });
    },
  }),

  asCovidTestResult: trait({
    afterCreate(userDocument, server) {
      const userDocumentTemplate =
        server.schema.userDocumentTemplates.findBy({ identifier: 'covid-19-test-result' }) ??
        server.create('user-document-template', 'asCovidTestResult');
      const inputFieldsData = userDocument?.inputFieldsData;
      const issueDate =
        inputFieldsData?.issue_date ??
        format(faker.date.past({ years: 2, refDate: userDocument.createdAt }), DATE_FORMAT);
      userDocument.update({
        userDocumentTemplateId: userDocumentTemplate.id,
        inputFieldsData: {
          ...inputFieldsData,
          issue_date: issueDate,
        },
      });

      let location = userDocument.location;

      if (!location) {
        const company = server.schema.companies.first() ?? server.create('company', 'withLocation');
        location = company.locations.models[0];
      }

      server.create('user-document-location-context', 'asReview', 'asWillExpire', {
        userDocument,
        location: location ?? null,
      });
    },
  }),

  asCovidVaccineCard: trait({
    afterCreate(userDocument, server) {
      const userDocumentTemplate =
        server.schema.userDocumentTemplates.findBy({ identifier: 'covid-19-vaccine-card' }) ??
        server.create('user-document-template', 'asCovidVaccineCard');

      const inputFieldsData = userDocument?.inputFieldsData;
      const lastDoseDate =
        inputFieldsData?.last_dose_date ??
        format(faker.date.past({ years: 2, refDate: userDocument.createdAt }), DATE_FORMAT);
      userDocument.update({
        userDocumentTemplateId: userDocumentTemplate.id,
        inputFieldsData: {
          ...inputFieldsData,
          last_dose_date: lastDoseDate,
        },
      });

      let location = userDocument.location;

      if (!location) {
        const company = server.schema.companies.first() ?? server.create('company', 'withLocation');
        location = company.locations.models[0];
      }

      server.create('user-document-location-context', 'asReview', {
        userDocument,
        location: location ?? null,
      });
    },
  }),

  asExpired: trait({
    afterCreate(userDocument) {
      const userDocumentLocationContexts = userDocument?.userDocumentLocationContexts?.models || [];
      const [context] = userDocumentLocationContexts;

      context?.update({
        expirationDate: () => format(faker.date.past(), 'yyyy-MM-dd'),
        expired: true,
      });
    },
  }),

  asExpiring: trait({
    afterCreate(userDocument) {
      const userDocumentLocationContexts = userDocument?.userDocumentLocationContexts?.models || [];
      const [context] = userDocumentLocationContexts;

      context?.update({
        expirationDate: format(new Date(), 'yyyy-MM-dd'),
        expired: false,
      });
    },
  }),

  afterCreate(userDocument, server) {
    const user = userDocument.user ?? server.schema.users.first();

    userDocument.update({
      userId: user?.id,
    });
  },
});
